import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { headerNavigationSlice } from "../../../common/Header/redux/slice";
import type { ServiceLinksType } from "../../../common/Header/types";
import { recreateAllApis } from "../../../services/api/config/apiConfig";
import type { TApiName } from "../../../services/api/config/types";
import { APP_MULTI_USER_API } from "../../../services/api/constants";
import { recreateAppKeycloakApi } from "../../../services/api/instances/keycloakApiInstance";
import { getUserInfo } from "../../../services/api/methods";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { multiUserApi } from "../../../shared/utils/multiUserFunctions";
import { navigateToRoute } from "../../../shared/utils/navigate-to-route";
import { getAllSettingsObjectsAction } from "../redux/actions";
import { setIsUserInfoLoading, setUserInfo } from "../redux/slice";

export const useRestoreApp = () => {
  const dispatch = useAppDispatch();
  const [isApiCreated, setIsApiCreated] = useState(false);
  const navigate = useNavigate();

  const setUserInfoCallback = useCallback(() => {
    dispatch(setUserInfo(null));
  }, [dispatch]);

  const recreateApi = () => {
    if (!isApiCreated) {
      recreateAppKeycloakApi(setUserInfoCallback);
      recreateAllApis(setUserInfoCallback, false);
      setIsApiCreated(true);
    }
  };

  const restoreApp = () => {
    if (isApiCreated) {
      getUserInfo()
        .then((userInfo) => {
          dispatch(setUserInfo(userInfo));
          dispatch(setIsUserInfoLoading(false));
          dispatch(getAllSettingsObjectsAction(userInfo.preferred_username))
            .unwrap()
            .then((res) => {
              const links =
                res?.settingsObject?.value?.user_settings?.app_links;
              dispatch(
                headerNavigationSlice.actions.initializeVisibleLinks(
                  links as ServiceLinksType,
                ),
              );
              navigateToRoute(links as ServiceLinksType, navigate);
            });
          if (userInfo.replacement_token) {
            localStorage.setItem(
              APP_MULTI_USER_API,
              multiUserApi.setUserKey(
                multiUserApi.parseToArray(),
                "replacement_token",
                userInfo.replacement_token,
              ),
            );
          }
          const baseUrlOverrides: Partial<Record<TApiName, string>> = {};
          if (userInfo.thread_url)
            baseUrlOverrides.thread = userInfo.thread_url;
          if (userInfo.thread_model_url)
            baseUrlOverrides.model = userInfo.thread_model_url;
          if (userInfo.tg_bot_service_url)
            baseUrlOverrides.bot = userInfo.tg_bot_service_url;

          recreateAllApis(
            setUserInfoCallback,
            !!userInfo.replacement_token,
            baseUrlOverrides,
          );
        })
        .catch(() => {
          dispatch(setIsUserInfoLoading(false));
          dispatch(setUserInfo(null));
        });
    }
  };

  return { isApiCreated, recreateApi, restoreApp };
};
